import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { Subscription } from 'rxjs';
import { app_permissions } from '../../../security/app-permissions';
import { PermissionService } from '../../../security/permission.service';
import { PageDataPanelComponent } from '../../../shared/page-layout/page-data-panel.component';
import { MyHrWorkspaceOverview } from '../shared/my-hr-workspace.models';
import { MyHrWorkspaceService } from '../shared/my-hr-workspace.service';
import { labelize, statusSeverity } from '../shared/my-hr-workspace.utils';

interface MyHrLauncherCard {
  title: string;
  description: string;
  route: string;
  icon: string;
  badge?: number;
  accent: 'teal' | 'amber' | 'blue' | 'slate';
  visible: boolean;
}

@Component({
  selector: 'app-my-hr-home',
  standalone: true,
  imports: [CommonModule, RouterLink, ButtonModule, TagModule, PageDataPanelComponent],
  templateUrl: './my-hr-home.component.html',
  styleUrls: ['./my-hr-home.component.scss']
})
export class MyHrHomeComponent implements OnInit, OnDestroy {
  private readonly permissionService = inject(PermissionService);
  private readonly workspaceService = inject(MyHrWorkspaceService);
  private readonly subscriptions = new Subscription();

  readonly canViewSelf = this.permissionService.hasPermission(app_permissions.hr.self.view);
  readonly canViewManager = this.permissionService.hasPermission(app_permissions.hr.manager.view);
  readonly canViewExpenses = this.permissionService.hasPermission(app_permissions.expense.records.view);
  readonly canManageExpenses = this.permissionService.hasPermission(app_permissions.expense.records.create);
  readonly canApproveExpenses = this.permissionService.hasPermission(app_permissions.expense.approval.view);

  loading = false;
  overview?: MyHrWorkspaceOverview;

  ngOnInit(): void {
    this.load();
    this.subscriptions.add(this.workspaceService.refreshRequested$.subscribe(() => this.load()));
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  get cards(): MyHrLauncherCard[] {
    const overview = this.overview;
    const cards: MyHrLauncherCard[] = [
      {
        title: 'Profile',
        description: 'Personal HR profile, contact details, and notification preferences.',
        route: '/my-hr/profile',
        icon: 'fa-solid fa-id-badge',
        accent: 'teal',
        visible: true
      },
      {
        title: 'Documents',
        description: 'View your HR documents and upload pending files for review.',
        route: '/my-hr/documents',
        icon: 'fa-solid fa-folder-open',
        badge: overview?.documentsCount,
        accent: 'blue',
        visible: true
      },
      {
        title: 'Leave',
        description: 'Request time off, review balances, and track approvals.',
        route: '/my-hr/leave',
        icon: 'fa-solid fa-calendar-day',
        badge: overview?.pendingLeaveCount || overview?.leaveCount,
        accent: 'amber',
        visible: true
      },
      {
        title: 'Approvals',
        description: 'Track your requests and monitor manager or finance approval queues.',
        route: '/my-hr/approvals',
        icon: 'fa-solid fa-clipboard-check',
        badge: (overview?.approvalsCount || 0) + (overview?.pendingExpenseApprovals || 0),
        accent: 'teal',
        visible: true
      },
      {
        title: 'Timesheets',
        description: 'Review captured time, hours, and current timesheet status.',
        route: '/my-hr/timesheets',
        icon: 'fa-solid fa-clock',
        badge: overview?.timesheetCount,
        accent: 'slate',
        visible: true
      },
      {
        title: 'Payslips',
        description: 'Open payroll payslips and see published net pay history.',
        route: '/my-hr/payslips',
        icon: 'fa-solid fa-wallet',
        badge: overview?.payslipCount,
        accent: 'blue',
        visible: true
      },
      {
        title: 'Learning',
        description: 'Check required training, due dates, and completed learning.',
        route: '/my-hr/learning',
        icon: 'fa-solid fa-graduation-cap',
        badge: overview?.learningCount,
        accent: 'teal',
        visible: true
      },
      {
        title: 'Expenses',
        description: 'Create quick claims and track reimbursement approvals.',
        route: '/my-hr/expenses',
        icon: 'fa-solid fa-receipt',
        badge: overview?.expenseCount,
        accent: 'amber',
        visible: this.canViewExpenses || this.canManageExpenses
      },
      {
        title: 'Manager Team',
        description: 'See direct reports and the current manager workload.',
        route: '/my-hr/team',
        icon: 'fa-solid fa-people-group',
        badge: overview?.teamCount,
        accent: 'slate',
        visible: this.canViewManager
      }
    ];
    return cards.filter(card => card.visible);
  }

  statusSeverity(value?: string): 'success' | 'secondary' | 'info' | 'warn' | 'danger' {
    return statusSeverity(value);
  }

  label(value?: string): string {
    return labelize(value);
  }

  private load(): void {
    this.loading = true;
    this.workspaceService.loadOverview(
      this.canViewSelf,
      this.canViewManager,
      this.canViewExpenses || this.canManageExpenses,
      this.canApproveExpenses
    ).subscribe({
      next: overview => {
        this.overview = overview;
      },
      error: () => {
        this.overview = undefined;
      },
      complete: () => {
        this.loading = false;
      }
    });
  }
}
